/**
 * Date filtering utilities for QAQC PDF Report generation
 */

/**
 * Filter dataset by received_date field
 * @param {Array} data - Array of data objects
 * @param {string} dateFrom - Start date (YYYY-MM-DD) or empty for no limit
 * @param {string} dateTo - End date (YYYY-MM-DD) or empty for no limit
 * @returns {Array} Filtered data
 */
export const filterByDateRange = (data, dateFrom, dateTo) => {
  if (!data || !Array.isArray(data)) return [];
  if (!dateFrom && !dateTo) return data;
  
  return data.filter(item => {
    if (!item.received_date) return false;
    
    try {
      const itemDate = new Date(item.received_date);
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        if (itemDate < fromDate) return false;
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo);
        // Set to end of day for inclusive comparison
        toDate.setHours(23, 59, 59, 999);
        if (itemDate > toDate) return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Invalid date in data:', item.received_date);
      return false;
    }
  });
};

/**
 * Get date range summary for report header
 * @param {string} dateFrom - Start date
 * @param {string} dateTo - End date
 * @returns {string} Formatted date range summary
 */
export const getDateRangeSummary = (dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return 'All Data to Date';
  if (!dateFrom) return `Data up to ${dateTo}`;
  if (!dateTo) return `Data from ${dateFrom}`;
  return `${dateFrom} to ${dateTo}`;
};

/**
 * Filter all dashboard datasets by date range
 * @param {Object} dashboardData - All dashboard data
 * @param {string} dateFrom - Start date
 * @param {string} dateTo - End date
 * @returns {Object} Filtered dashboard data
 */
export const filterDashboardDataByDate = (dashboardData, dateFrom, dateTo) => {
  if (!dashboardData) return {};
  
  const filtered = {};
  
  // Filter each dataset that has received_date
  for (const [key, data] of Object.entries(dashboardData)) {
    if (Array.isArray(data)) {
      filtered[key] = filterByDateRange(data, dateFrom, dateTo);
    } else {
      filtered[key] = data; // Keep non-array data as-is
    }
  }
  
  return filtered;
};

/**
 * Get the earliest and latest dates from a dataset
 * @param {Array} data - Array of data objects with received_date
 * @returns {Object} { minDate, maxDate } or null if no valid dates
 */
export const getDateRange = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  
  const dates = data
    .filter(item => item.received_date)
    .map(item => new Date(item.received_date))
    .filter(date => !isNaN(date.getTime()));
  
  if (dates.length === 0) return null;
  
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));
  
  return {
    minDate: minDate.toISOString().split('T')[0],
    maxDate: maxDate.toISOString().split('T')[0]
  };
};

/**
 * Format a value for chart category axes: date only, no time-of-day.
 * Handles ISO (`2025-09-01T00:00:00`), PostgreSQL (`2025-09-01 00:00:00`), and plain dates.
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export const formatChartAxisDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && !Number.isNaN(value)) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toISOString().split('T')[0];
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }
  return s;
};

/**
 * Format date for display
 * @param {string} dateString - Date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    return dateString;
  }
};
